import React from 'react';
import { Button, ButtonProps, Box } from '@mui/material';
import { styled } from '@mui/material/styles';


export type ActionButtonAppVariant = 'ready' | 'loading' | 'deactivated';
export type ActionButtonType = 'discutez' | 'activer' | 'preparation';

interface ActionStateButtonProps extends Omit<ButtonProps, 'variant'> {
  selectedFolderId: string;
  selectedSubFolderId: string;
  appVariant: ActionButtonAppVariant;
  buttonType: ActionButtonType;
  children?: React.ReactNode;
}

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    padding: '12px 4px',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    transition: theme.transitions.create(['background-color', 'border-color', 'color', 'opacity'], {
        duration: theme.transitions.duration.short,
    }),
}));

const StyledMuiButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'appVariant' && prop !== 'buttonType',
})< Pick<ActionStateButtonProps, 'appVariant' | 'buttonType' >>(({ theme, appVariant, buttonType }) => {
  const baseStyles = {
    display: 'flex',
    height: '26px',
    width: '96px',
    padding: '4px 7px',
    justifyContent: 'center',
    alignItems: 'center',
    '@media (max-width: 370px)': {
      width: '40px',
      height: '20px',
      fontSize: '10px',
    },
    gap: '10px',
    borderRadius: '20px',
    border: '1px solid var(--gradient-electric, #2563EB)',
    background: 'linear-gradient(90deg, #E4ECFF 0%, #EADBFF 100%)',
    boxShadow: '0px 0px 2px 0px rgba(37, 99, 235, 0.25)',
    alignSelf: 'stretch',
    transition: theme.transitions.create(['background-color', 'border-color', 'color', 'opacity'], {
        duration: theme.transitions.duration.short,
    }),
  };

  if (appVariant === 'deactivated') {
    return {
      ...baseStyles,
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
      cursor: 'not-allowed',
      opacity: 0.7,
    };
  }

  if (appVariant === 'loading' && buttonType === 'preparation') {
    return {
      ...baseStyles,
      background : "linear-gradient(to right, #A5B9FF, #C2A4FF)", // Lavender
      color: 'white !important',
    };
  }


  // Styles for 'ready' variant and potentially 'loading' if not 'preparation'
  if (buttonType === 'discutez') {
    return {
      ...baseStyles,
      background: 'linear-gradient(to right, #4A00E0, #8E2DE2)',
      color: theme.palette.common.white,
      '&:hover': {
        background: 'linear-gradient(to right, #3E00B3, #7224B5)',
      },
    };
  }

  if (buttonType === 'activer') {
    return {
      ...baseStyles,
      borderColor: 'linear-gradient(91deg, #2563EB 5.84%, #7C3AED 94.16%);',
      color: '#2563EB',
      border: "1px solid var(--gradient-electric, #2563EB);",
      background: 'linear-gradient(90deg, #E4ECFF 0%, #EADBFF 100%);',
      '&:hover': {
        backgroundColor: 'rgba(160, 196, 255, 0.1)',
        borderColor: '#8FAEE0',
        color: '#8FAEE0',
      },
    };
  }
  return baseStyles;
});

export const ActionStateButton: React.FC<ActionStateButtonProps> = ({
  appVariant,
  buttonType,
  children,
  onClick,
  disabled,
  selectedFolderId,
  selectedSubFolderId,
  ...rest
}) => {

  const isActuallyDisabled = disabled || appVariant === 'deactivated';

  return (
    <StyledBox>
        <StyledMuiButton
        appVariant={appVariant}
        buttonType={buttonType}
        onClick={onClick}
        disabled={isActuallyDisabled}
        {...rest}
        >
        {children}
        </StyledMuiButton>
    </StyledBox>
  );
};
